import { useState } from 'react';
import type { KeyboardEvent } from 'react';
import { Link } from 'react-router';
import { Button, Checkbox, InlineError, Input, Select, Skeleton } from '../../components/ui';
import { localIsoDate } from '../myTasks/types';
import { useDealSections, useDealStages, useDeals } from './hooks';
import {
  useCreateDealTask,
  useDealTasks,
  useSetDealTaskCompleted,
  useUpdateDealTask,
} from './dealTasks';
import { useTaskDefaults } from '../tasks/hooks';
import { taskErrorMessage } from '../tasks/queries';
import { dueDateProblem } from '../tasks/taskLens';
import { DealDrawer } from './DealDrawer';
import { OutboundPicker } from './OutboundPicker';
import { useOutboundEntities } from './outboundEntities';
import {
  DEAL_TASK_FILTERS,
  DEAL_TASK_FILTER_LABELS,
  filterDealTasks,
  sortDealTasks,
} from './dealTaskFilters';
import type { DealTaskFilter } from './dealTaskFilters';
import type { DealTask, DealTaskChanges } from './dealTasks';
import { isOverdue } from './types';
import type { Deal, Pipeline } from './types';
import type { ProjectMemberWithProfile } from '../projects/types';

/**
 * Deals › Tasks: a flat list.
 *
 * Kyu, Sep 8: "I need the task subtab under deals to look like a normal task
 * page where it's just a list of tasks, the deadlines, the name of the task,
 * and then when you click on it, it can link to whatever deal and lead it's
 * about. I don't like the deals auto-population."
 *
 * The previous shape listed every deal on the board as a heading, with an
 * "+ Add task" offer under each, whether or not the deal had a task. On a
 * pipeline with 40 deals and 3 tasks that is 40 headings and 40 offers around
 * 3 rows of content, which is a directory of deals with tasks as a footnote.
 *
 * This is the other way round. A row is a task. The deal is a column, and the
 * date is the order. Nothing appears for a deal with no task. The three ways
 * out of a row are the three things Kyu named:
 *
 *   the task name  → the task's own form (title, assignee, due, contact, company)
 *   the deal name  → the deal drawer, right here, without leaving the tab
 *   the contact    → the lead's page in the Tracker
 *
 * Contact and Company stay on the row, as Kyu asked ("both"), but they are
 * read-only there now: changing them is what the task form is for, so the row
 * has exactly one behaviour per cell.
 *
 * Every row is still a real task in public.tasks with deal_id set, so it still
 * turns up in the assignee's My Tasks. The mutations are the same ones as
 * before, plus one general edit; nothing about the data moved.
 */

export function DealTasksView({
  pipeline,
  people,
}: {
  pipeline: Pipeline;
  people: ProjectMemberWithProfile[];
}) {
  const deals = useDeals(pipeline);
  const sections = useDealSections(pipeline);
  const stages = useDealStages(pipeline);
  const dealIds = (deals.data ?? []).map((deal) => deal.id);
  const tasks = useDealTasks(pipeline, dealIds);
  const setCompleted = useSetDealTaskCompleted();
  const outbound = useOutboundEntities();
  const [error, setError] = useState('');
  const [filter, setFilter] = useState<DealTaskFilter>('open');
  // One thing open at a time: a task's form, the composer, or a deal drawer.
  const [editing, setEditing] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [openDealId, setOpenDealId] = useState<string | null>(null);

  if (deals.isPending || sections.isPending) {
    return <Skeleton lines={4} label="Loading deal tasks" />;
  }
  if (deals.isError || sections.isError) {
    return <InlineError>Deal tasks could not be loaded.</InlineError>;
  }

  const today = localIsoDate(new Date());
  const all = tasks.data ?? [];
  const rows = sortDealTasks(filterDealTasks(all, filter, today), today);
  const dealById = new Map(deals.data.map((deal) => [deal.id, deal]));
  const openDeal = openDealId === null ? undefined : dealById.get(openDealId);
  const highestPosition = all.reduce((high, task) => Math.max(high, task.position), 0);

  const personLabel = (id: string | null) => {
    const person = people.find((row) => row.user_id === id);
    if (person === undefined) {
      return null;
    }
    return person.display_name.trim() === '' ? person.email : person.display_name;
  };

  function toggle(taskId: string, isCompleted: boolean) {
    setError('');
    setCompleted.mutate(
      { taskId, isCompleted },
      { onError: (cause: Error) => setError(taskErrorMessage(cause.message)) },
    );
  }

  return (
    <>
      {error === '' ? null : <InlineError>{error}</InlineError>}

      <div className="deal-tasks-filters">
        {DEAL_TASK_FILTERS.map((key) => (
          <Button
            key={key}
            variant={filter === key ? 'primary' : 'secondary'}
            size="sm"
            onClick={() => setFilter(key)}
          >
            {DEAL_TASK_FILTER_LABELS[key]} ({filterDealTasks(all, key, today).length})
          </Button>
        ))}
      </div>

      {/* The card is what makes the alternating rows readable: their fill is a
          small step down from white and resolves to nothing on the page. Fill
          and no stroke, like All Deals. See .deal-tasks-card. */}
      <div className="deal-tasks-card">
        <table className="ui-table deal-tasks-table">
          <colgroup>
            <col className="deal-tasks-col-done" />
            <col />
            <col className="deal-tasks-col-deal" />
            <col className="deal-tasks-col-who" />
            <col className="deal-tasks-col-due" />
            <col className="deal-tasks-col-who" />
            <col className="deal-tasks-col-who" />
          </colgroup>
          <thead>
            <tr>
              <th>
                <span className="ui-visually-hidden">Done</span>
              </th>
              <th>Task</th>
              <th>Deal</th>
              <th>Assignee</th>
              <th>Due</th>
              <th>Contact</th>
              <th>Company</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((task) =>
              editing === task.id ? (
                <EditTaskRow
                  key={task.id}
                  task={task}
                  dealName={dealById.get(task.deal_id)?.name ?? '—'}
                  people={people}
                  outbound={outbound}
                  onDone={() => setEditing(null)}
                  onError={setError}
                />
              ) : (
                <tr key={task.id} className={task.is_completed ? 'deal-tasks-done' : undefined}>
                  <td>
                    <Checkbox
                      label={`Complete ${task.title}`}
                      labelHidden
                      checked={task.is_completed}
                      onChange={() => toggle(task.id, !task.is_completed)}
                    />
                  </td>
                  <th scope="row">
                    <button
                      type="button"
                      className="deal-tasks-open"
                      aria-label={`Edit ${task.title}`}
                      onClick={() => {
                        setAdding(false);
                        setEditing(task.id);
                      }}
                    >
                      {task.title}
                    </button>
                  </th>
                  <td>
                    <DealCell deal={dealById.get(task.deal_id)} onOpen={setOpenDealId} />
                  </td>
                  <td>
                    {personLabel(task.assignee_id) ?? (
                      <span className="deal-note-empty">Unassigned</span>
                    )}
                  </td>
                  <td>
                    {/* Plain text, red when late. Every due date used to be a
                        Badge — twelve chips in twelve rows — and the one that
                        mattered was a red chip among grey ones. docs/design-rule.md:
                        a value on most rows is plain text at one of two ink
                        weights, never a chip; colour is spent on exceptions.
                        Same treatment as the task table and Sales Tasks. The
                        word stays for a screen reader. */}
                    {task.due_date === null ? (
                      <span className="deal-note-empty">—</span>
                    ) : !task.is_completed && isOverdue(task.due_date, today) ? (
                      <span className="deal-tasks-due deal-tasks-due--overdue">
                        {task.due_date}
                        <span className="ui-visually-hidden"> (overdue)</span>
                      </span>
                    ) : (
                      <span className="deal-tasks-due">{task.due_date}</span>
                    )}
                  </td>
                  <td>
                    <ContactCell leadId={task.lead_id} leads={outbound.leads} />
                  </td>
                  <td>
                    {outbound.companies.find((row) => row.id === task.company_id)?.label ?? (
                      <span className="deal-note-empty">—</span>
                    )}
                  </td>
                </tr>
              ),
            )}

            {adding ? (
              <AddTaskRow
                deals={deals.data}
                people={people}
                highestPosition={highestPosition}
                onDone={() => setAdding(false)}
                onError={setError}
              />
            ) : (
              <tr className="deal-tasks-add-row">
                <td colSpan={7}>
                  <button
                    type="button"
                    className="deal-tasks-add-trigger"
                    onClick={() => {
                      setEditing(null);
                      setAdding(true);
                    }}
                  >
                    + Add task
                  </button>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {rows.length === 0 && !adding ? (
        <p className="task-panel-text">
          {filter === 'open'
            ? 'No open tasks.'
            : `Nothing matches ${DEAL_TASK_FILTER_LABELS[filter].toLowerCase()}.`}
        </p>
      ) : null}

      {openDeal === undefined ? null : (
        <DealDrawer
          key={openDeal.id}
          deal={openDeal}
          sections={sections.data}
          stages={stages.data ?? []}
          people={people}
          pipeline={pipeline}
          onClose={() => setOpenDealId(null)}
        />
      )}
    </>
  );
}

/** The deal a task belongs to, as the way into that deal. */
function DealCell({ deal, onOpen }: { deal: Deal | undefined; onOpen: (id: string) => void }) {
  if (deal === undefined) {
    return <span className="deal-note-empty">—</span>;
  }
  return (
    <button
      type="button"
      className="deal-tasks-open"
      aria-label={`Open deal ${deal.name}`}
      onClick={() => onOpen(deal.id)}
    >
      {deal.name}
    </button>
  );
}

/**
 * The contact, as the way to the lead's page. /tracker?lead=<id> is the shape
 * a lead notification already uses, so it opens the lead rather than the list.
 */
function ContactCell({
  leadId,
  leads,
}: {
  leadId: string | null;
  leads: { id: string; label: string }[];
}) {
  const lead = leads.find((row) => row.id === leadId);
  if (lead === undefined) {
    return <span className="deal-note-empty">—</span>;
  }
  return (
    <Link className="deal-tasks-contact" to={`/tracker?lead=${lead.id}`}>
      {lead.label}
    </Link>
  );
}

type Outbound = ReturnType<typeof useOutboundEntities>;

function assigneeOptions(people: ProjectMemberWithProfile[]) {
  return [
    { value: '', label: 'Unassigned' },
    ...people.map((person) => ({
      value: person.user_id,
      label: person.display_name.trim() === '' ? person.email : person.display_name,
    })),
  ];
}

/**
 * The task's own form, in its row. The five fields are the five columns, so
 * the form is the row with its cells unlocked rather than a second layout.
 * Enter saves, Escape leaves it as it was.
 */
function EditTaskRow({
  task,
  dealName,
  people,
  outbound,
  onDone,
  onError,
}: {
  task: DealTask;
  /** Shown, not editable: moving a task between deals is not a thing this form does. */
  dealName: string;
  people: ProjectMemberWithProfile[];
  outbound: Outbound;
  onDone: () => void;
  onError: (message: string) => void;
}) {
  const updateTask = useUpdateDealTask();
  const workspace = useTaskDefaults();
  const [title, setTitle] = useState(task.title);
  const [assignee, setAssignee] = useState(task.assignee_id ?? '');
  const [due, setDue] = useState(task.due_date ?? '');
  const [leadId, setLeadId] = useState(task.lead_id);
  const [companyId, setCompanyId] = useState(task.company_id);
  const [titleProblem, setTitleProblem] = useState('');
  const [dueProblem, setDueProblem] = useState('');

  function save() {
    if (updateTask.isPending) {
      return;
    }
    onError('');
    if (title.trim() === '') {
      setTitleProblem('Enter a task name.');
      return;
    }
    const dueDate = due === '' ? null : due;
    const problem = dueDateProblem(dueDate, workspace.requireDueDate);
    if (problem !== null) {
      setDueProblem(problem);
      return;
    }
    // Only what changed is written, so an untouched field can never overwrite
    // an edit somebody else made to it in the meantime.
    const changes: DealTaskChanges = {};
    if (title.trim() !== task.title) changes.title = title.trim();
    const assigneeId = assignee === '' ? null : assignee;
    if (assigneeId !== task.assignee_id) changes.assignee_id = assigneeId;
    if (dueDate !== task.due_date) changes.due_date = dueDate;
    if (leadId !== task.lead_id) changes.lead_id = leadId;
    if (companyId !== task.company_id) changes.company_id = companyId;
    if (Object.keys(changes).length === 0) {
      onDone();
      return;
    }
    updateTask.mutate(
      { taskId: task.id, changes },
      {
        onSuccess: onDone,
        onError: (cause: Error) => onError(taskErrorMessage(cause.message)),
      },
    );
  }

  const keys = (event: KeyboardEvent) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      save();
    }
    if (event.key === 'Escape') {
      onDone();
    }
  };

  return (
    <>
      <tr className="deal-tasks-composer">
        <td />
        <td>
          <Input
            label="Task"
            labelHidden
            value={title}
            autoFocus
            {...(titleProblem === '' ? {} : { error: titleProblem })}
            onChange={(event) => {
              setTitle(event.target.value);
              setTitleProblem('');
            }}
            onKeyDown={keys}
          />
        </td>
        <td>
          <span className="deal-tasks-fixed">{dealName}</span>
        </td>
        <td>
          <Select
            label="Assignee"
            labelHidden
            value={assignee}
            options={assigneeOptions(people)}
            onChange={(event) => setAssignee(event.target.value)}
          />
        </td>
        <td>
          <Input
            label="Due date"
            labelHidden
            type="date"
            value={due}
            {...(dueProblem === '' ? {} : { error: dueProblem })}
            onChange={(event) => {
              setDue(event.target.value);
              setDueProblem('');
            }}
            onKeyDown={keys}
          />
        </td>
        <td>
          <OutboundPicker
            label={`Contact for ${task.title}`}
            emptyLabel="No contact"
            entities={outbound.leads}
            selectedId={leadId}
            canReachOutbound={outbound.canReachOutbound}
            onChange={setLeadId}
          />
        </td>
        <td>
          <OutboundPicker
            label={`Company for ${task.title}`}
            emptyLabel="No company"
            entities={outbound.companies}
            selectedId={companyId}
            canReachOutbound={outbound.canReachOutbound}
            onChange={setCompanyId}
          />
        </td>
      </tr>
      <tr className="deal-tasks-composer-foot">
        <td />
        <td colSpan={6}>
          <div className="deal-tasks-composer-actions">
            <Button size="sm" onClick={save} disabled={updateTask.isPending}>
              {updateTask.isPending ? 'Saving…' : 'Save'}
            </Button>
            <Button variant="ghost" size="sm" onClick={onDone}>
              Cancel
            </Button>
          </div>
        </td>
      </tr>
    </>
  );
}

/**
 * The one composer, at the foot of the list. You say which deal the task is
 * for; the list no longer says it for you by putting a form under every deal.
 */
function AddTaskRow({
  deals,
  people,
  highestPosition,
  onDone,
  onError,
}: {
  deals: Deal[];
  people: ProjectMemberWithProfile[];
  highestPosition: number;
  onDone: () => void;
  onError: (message: string) => void;
}) {
  const createTask = useCreateDealTask();
  const workspace = useTaskDefaults();
  const [title, setTitle] = useState('');
  const [dealId, setDealId] = useState('');
  const [assignee, setAssignee] = useState('');
  // undefined = untouched, take the workspace default; '' = cleared.
  const [due, setDue] = useState<string | undefined>(undefined);
  const [titleProblem, setTitleProblem] = useState('');
  const [dealProblem, setDealProblem] = useState('');
  const [dueProblem, setDueProblem] = useState('');
  const effectiveDue = due ?? workspace.defaultDueDate ?? '';

  // Open deals first, since that is where new work goes; closed ones after,
  // still reachable, because a won deal can have a follow-up.
  const dealOptions = [
    { value: '', label: 'Pick a deal' },
    ...[...deals]
      .sort((a, b) => {
        const aOpen = a.outcome === 'open' ? 0 : 1;
        const bOpen = b.outcome === 'open' ? 0 : 1;
        return aOpen - bOpen || a.name.localeCompare(b.name);
      })
      .map((deal) => ({
        value: deal.id,
        label: deal.outcome === 'open' ? deal.name : `${deal.name} (${deal.outcome})`,
      })),
  ];

  function add() {
    // Every branch ends in something visible. `if (title.trim() === '') return`
    // is indistinguishable from a broken button, which is what it used to be.
    if (createTask.isPending) {
      return;
    }
    onError('');
    if (title.trim() === '') {
      setTitleProblem('Enter a task name.');
      return;
    }
    if (dealId === '') {
      setDealProblem('Pick the deal this is for.');
      return;
    }
    const dueDate = effectiveDue === '' ? null : effectiveDue;
    const problem = dueDateProblem(dueDate, workspace.requireDueDate);
    if (problem !== null) {
      setDueProblem(problem);
      return;
    }
    createTask.mutate(
      {
        dealId,
        title: title.trim(),
        assigneeId: assignee === '' ? null : assignee,
        dueDate,
        position: highestPosition + 10,
      },
      {
        onSuccess: onDone,
        // The workspace due-date rule is enforced by a trigger and this form
        // can disagree with it whenever workspace_settings has not loaded, so
        // a refusal is the expected way that disagreement surfaces.
        onError: (cause: Error) => onError(taskErrorMessage(cause.message)),
      },
    );
  }

  const keys = (event: KeyboardEvent) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      add();
    }
    if (event.key === 'Escape') {
      onDone();
    }
  };

  return (
    <tr className="deal-tasks-composer">
      <td />
      <td>
        <Input
          label="New task"
          labelHidden
          placeholder="What needs doing?"
          value={title}
          autoFocus
          {...(titleProblem === '' ? {} : { error: titleProblem })}
          onChange={(event) => {
            setTitle(event.target.value);
            setTitleProblem('');
          }}
          onKeyDown={keys}
        />
      </td>
      <td>
        <Select
          label="Deal"
          labelHidden
          value={dealId}
          options={dealOptions}
          {...(dealProblem === '' ? {} : { error: dealProblem })}
          onChange={(event) => {
            setDealId(event.target.value);
            setDealProblem('');
          }}
        />
      </td>
      <td>
        <Select
          label="Assignee"
          labelHidden
          value={assignee}
          options={assigneeOptions(people)}
          onChange={(event) => setAssignee(event.target.value)}
        />
      </td>
      <td>
        <Input
          label="Due date"
          labelHidden
          type="date"
          value={effectiveDue}
          {...(dueProblem === '' ? {} : { error: dueProblem })}
          onChange={(event) => {
            setDue(event.target.value);
            setDueProblem('');
          }}
          onKeyDown={keys}
        />
      </td>
      <td colSpan={2}>
        <div className="deal-tasks-composer-actions">
          <Button size="sm" onClick={add} disabled={createTask.isPending}>
            {createTask.isPending ? 'Adding…' : 'Add task'}
          </Button>
          <Button variant="ghost" size="sm" onClick={onDone}>
            Cancel
          </Button>
        </div>
      </td>
    </tr>
  );
}
